import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronLeft,
    FiChevronRight,
    FiFileText,
    FiRefreshCw,
    FiSearch,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchPurchaseDebitNoteReport,
    purchaseDebitNoteRecordsSelector,
    purchaseDebitNoteSummarySelector,
    purchaseDebitNotePaginationSelector,
    purchaseReportLoadingSelector,
    purchaseReportErrorSelector,
} from "../../../../../../../store/billing/purchaseReport/purchaseReportSlice";


const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};


const DebitNoteReport = ({
    fromDate,
    toDate,
}) => {
    const dispatch =
        useDispatch();

    const records =
        useSelector(
            purchaseDebitNoteRecordsSelector
        ) || [];

    const summary =
        useSelector(
            purchaseDebitNoteSummarySelector
        ) || {};

    const pagination =
        useSelector(
            purchaseDebitNotePaginationSelector
        ) || {};

    const loading =
        useSelector(
            purchaseReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            purchaseReportErrorSelector
        ) || {};

    const [
        searchInput,
        setSearchInput,
    ] = useState("");

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState("ALL");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);


    const loadData =
        useCallback(
            () => {
                dispatch(
                    fetchPurchaseDebitNoteReport({
                        page,
                        limit,
                        fromDate,
                        toDate,
                        search,
                        status,
                    })
                );
            },
            [
                dispatch,
                page,
                limit,
                fromDate,
                toDate,
                search,
                status,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        setSearch(
                            searchInput.trim()
                        );

                        setPage(1);
                    },
                    350
                );

            return () =>
                clearTimeout(timer);
        },
        [
            searchInput,
        ]
    );


    const total =
        toNumber(
            pagination?.total
        );

    const currentPage =
        toNumber(
            pagination?.page,
            page
        ) || 1;

    const totalPages =
        Math.max(
            toNumber(
                pagination?.totalPages,
                1
            ),
            1
        );


    return (
        <div>
            {errors.debitNotes && (
                <div className="purchase-report-alert">
                    <FiAlertCircle />

                    <span>
                        {errors.debitNotes}
                    </span>
                </div>
            )}


            <section className="purchase-report-summary-grid">

                <div className="purchase-report-summary-card">
                    <span>
                        Debit Notes
                    </span>

                    <strong>
                        {toNumber(
                            summary?.totalDebitNotes
                        )}
                    </strong>

                    <small>
                        Total debit notes
                    </small>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        Confirmed
                    </span>

                    <strong>
                        {toNumber(
                            summary?.confirmed
                        )}
                    </strong>

                    <small>
                        Confirmed purchase returns
                    </small>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        Confirmed Debit
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.confirmedDebitAmount
                        )}
                    </strong>

                    <small>
                        Active debit value
                    </small>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        Cancelled
                    </span>

                    <strong>
                        {toNumber(
                            summary?.cancelled
                        )}
                    </strong>

                    <small>
                        Reversed debit notes
                    </small>
                </div>

            </section>


            <section className="purchase-report-toolbar">

                <div className="purchase-report-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            searchInput
                        }
                        placeholder="Search debit note, bill, supplier..."
                        onChange={(
                            event
                        ) =>
                            setSearchInput(
                                event.target.value
                            )
                        }
                    />
                </div>


                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setStatus(
                            event.target.value
                        );

                        setPage(1);
                    }}
                >
                    <option value="ALL">
                        All Status
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CONFIRMED">
                        Confirmed
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>
                </select>


                <button
                    type="button"
                    className="purchase-report-button"
                    onClick={
                        loadData
                    }
                >
                    <FiRefreshCw
                        className={
                            loading.debitNotes
                                ? "purchase-report-spinner"
                                : ""
                        }
                    />

                    Refresh
                </button>

            </section>


            <section className="purchase-report-table-card">

                {loading.debitNotes &&
                records.length === 0 ? (
                    <div className="purchase-report-empty">
                        <FiRefreshCw className="purchase-report-spinner" />

                        <strong>
                            Loading debit notes...
                        </strong>
                    </div>
                ) : records.length === 0 ? (
                    <div className="purchase-report-empty">
                        <FiFileText />

                        <strong>
                            No debit notes found
                        </strong>
                    </div>
                ) : (
                    <div className="purchase-report-table-wrapper">

                        <table className="purchase-report-table">

                            <thead>
                                <tr>
                                    <th>
                                        Debit Note
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Purchase Bill
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        Reason
                                    </th>

                                    <th className="text-right">
                                        Qty
                                    </th>

                                    <th className="text-right">
                                        Taxable
                                    </th>

                                    <th className="text-right">
                                        Tax
                                    </th>

                                    <th className="text-right">
                                        Debit
                                    </th>

                                    <th>
                                        Status
                                    </th>
                                </tr>
                            </thead>


                            <tbody>
                                {records.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                item?._id ||
                                                index
                                            }
                                        >
                                            <td>
                                                <strong className="purchase-report-bill-number">
                                                    {item?.debitNoteNumber || "-"}
                                                </strong>
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item?.debitNoteDate
                                                )}
                                            </td>

                                            <td>
                                                {item?.purchaseBillNumber || "-"}
                                            </td>

                                            <td>
                                                {item?.supplier?.supplierName || "-"}
                                            </td>

                                            <td>
                                                {String(
                                                    item?.reason || "-"
                                                ).replace(
                                                    /_/g,
                                                    " "
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {toNumber(
                                                    item?.totalQuantity
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.taxableAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.taxAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                <strong>
                                                    {formatCurrency(
                                                        item?.debitAmount
                                                    )}
                                                </strong>
                                            </td>

                                            <td>
                                                <span
                                                    className={`purchase-report-status purchase-report-status-${String(
                                                        item?.status || ""
                                                    ).toLowerCase()}`}
                                                >
                                                    {item?.status || "-"}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>

                        </table>

                    </div>
                )}


                <div className="purchase-report-pagination">

                    <div>
                        Total{" "}
                        <strong>
                            {total}
                        </strong>
                    </div>


                    <div className="purchase-report-pagination-controls">

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setLimit(
                                    Number(
                                        event.target.value
                                    )
                                );

                                setPage(1);
                            }}
                        >
                            <option value={10}>
                                10 / page
                            </option>

                            <option value={20}>
                                20 / page
                            </option>

                            <option value={50}>
                                50 / page
                            </option>
                        </select>


                        <button
                            type="button"
                            disabled={
                                currentPage <= 1
                            }
                            onClick={() =>
                                setPage(
                                    currentPage - 1
                                )
                            }
                        >
                            <FiChevronLeft />
                        </button>


                        <span>
                            Page{" "}
                            <strong>
                                {currentPage}
                            </strong>
                            {" of "}
                            <strong>
                                {totalPages}
                            </strong>
                        </span>


                        <button
                            type="button"
                            disabled={
                                currentPage >= totalPages
                            }
                            onClick={() =>
                                setPage(
                                    currentPage + 1
                                )
                            }
                        >
                            <FiChevronRight />
                        </button>

                    </div>

                </div>

            </section>
        </div>
    );
};


export default DebitNoteReport;